/**
 * Wishlist v1 (Guest/localStorage) + Cart Drawer hydrator
 * Optimized to prevent image flickering and layout shifts.
 */

const WISHLIST_KEY = "nani:wishlist:v1";
const WISHLIST_SYNC_ENDPOINT = "/apps/nani-app/save";
const WISHLIST_TOAST_SEEN_KEY = "nani:wishlist-toast-seen:v1";
let wishlistToastEl = null;
let wishlistToastTimer = null;

// --- Storage & State ---
const getWl = () => {
  try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || { items: [] }; } 
  catch { return { items: [] }; }
};
const setWl = (d) => localStorage.setItem(WISHLIST_KEY, JSON.stringify(d));
const hasItem = (id) => getWl().items.some((x) => String(x.id) === String(id));

const addItem = (item) => {
  const data = getWl();
  if (!data.items.some((x) => String(x.id) === String(item.id))) data.items.unshift(item);
  setWl(data);
  return data;
};

const removeItem = (id) => {
  const data = getWl();
  data.items = data.items.filter((x) => String(x.id) !== String(id));
  setWl(data);
  return data;
};

const toggleItem = (item) => {
  const exists = hasItem(item.id);
  return { exists, data: exists ? removeItem(item.id) : addItem(item) };
};

const hasSeenWishlistToast = () => {
  try { return localStorage.getItem(WISHLIST_TOAST_SEEN_KEY) === "1"; }
  catch { return false; }
};

const setSeenWishlistToast = () => {
  try { localStorage.setItem(WISHLIST_TOAST_SEEN_KEY, "1"); }
  catch {}
};

function syncSavedCartBadge() {
  const hasSavedItems = (getWl().items || []).length > 0;
  document.querySelectorAll(".cart-link__bubble").forEach((bubble) => {
    bubble.classList.toggle("cart-link__bubble--saved-visible", hasSavedItems);
  });
}

/**
 * UPDATED: dispatchWlEvent
 * Now triggers the sync whenever the wishlist changes.
 */
const dispatchWlEvent = (type, detail) => {
  window.dispatchEvent(new CustomEvent(type, { detail }));
  window.dispatchEvent(new CustomEvent("wishlist:change", { detail }));
  syncSavedCartBadge();
  
  // Sync logic
  const currentItems = getWl().items;
  syncWishlistToShopify(currentItems);
  pushWishlistToCartAttribute(currentItems).catch(err => console.warn("[Wishlist] Cart attribute sync failed:", err));

};

async function pushWishlistToCartAttribute(items) {
  await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      attributes: {
        "_nani_wishlist_cache": JSON.stringify(items)
      }
    })
  });
}

// --- Sync & Hydration ---

/**
 * Sync to Shopify App Proxy
 */
async function syncWishlistToShopify(items) {
  const customerId = window.ShopifyAnalytics?.meta?.page?.customerId || 
                     window.meta?.page?.customerId;

  if (!customerId) {
    console.info("[Wishlist] Skipping sync: no customer id on page context.");
    return;
  }

  try {
    const payload = {
      type: "wishlist",
      customer_id: customerId,
      wishlist: items
    };

    const response = await fetch(WISHLIST_SYNC_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let responseJson = null;
    try {
      responseJson = responseText ? JSON.parse(responseText) : null;
    } catch {
      responseJson = null;
    }

    if (!response.ok) {
      console.error("[Wishlist] Sync failed with non-2xx response.", {
        endpoint: WISHLIST_SYNC_ENDPOINT,
        status: response.status,
        statusText: response.statusText,
        customerId,
        payloadItemCount: items.length,
        responseBody: responseJson || responseText || null
      });
      return;
    }

    console.info("[Wishlist] Sync success.", {
      endpoint: WISHLIST_SYNC_ENDPOINT,
      status: response.status,
      customerId,
      payloadItemCount: items.length,
      responseBody: responseJson || responseText || null
    });
  } catch (err) {
    console.error("[Wishlist] Sync request error:", {
      endpoint: WISHLIST_SYNC_ENDPOINT,
      customerId,
      payloadItemCount: items.length,
      error: err
    });
  }
}

/**
 * Hydrate Wishlist from Shopify
 * Fetches cloud data and merges with local localStorage
 */
async function hydrateWishlist() {
  const customerId = window.ShopifyAnalytics?.meta?.page?.customerId || 
                     window.meta?.page?.customerId;

  if (!customerId) {
    console.info("[Wishlist] Skipping hydrate: no customer id on page context.");
    return;
  }

  try {
    const hydrateUrl = `${WISHLIST_SYNC_ENDPOINT}?customer_id=${customerId}`;
    const response = await fetch(hydrateUrl);
    const responseText = await response.text();
    let remoteData = null;
    try {
      remoteData = responseText ? JSON.parse(responseText) : null;
    } catch {
      remoteData = null;
    }

    if (!response.ok) {
      console.error("[Wishlist] Hydrate failed with non-2xx response.", {
        endpoint: hydrateUrl,
        status: response.status,
        statusText: response.statusText,
        customerId,
        responseBody: remoteData || responseText || null
      });
      return;
    }

    console.info("[Wishlist] Hydrate response received.", {
      endpoint: hydrateUrl,
      status: response.status,
      customerId,
      hasWishlistArray: Array.isArray(remoteData?.wishlist),
      wishlistCount: Array.isArray(remoteData?.wishlist) ? remoteData.wishlist.length : 0
    });

    if (remoteData.wishlist && Array.isArray(remoteData.wishlist)) {
      const localData = getWl();
      
      // Merge: Cloud data takes priority, but we keep unique local items
      const mergedItems = [...remoteData.wishlist];
      localData.items.forEach(localItem => {
        if (!mergedItems.some(remoteItem => String(remoteItem.id) === String(localItem.id))) {
          mergedItems.push(localItem);
        }
      });

      setWl({ items: mergedItems });
      renderSavedGrid(); // Update the UI with new merged data
      syncSavedCartBadge();
      
      // If we added new local items to the list, sync them back up
      if (mergedItems.length > remoteData.wishlist.length) {
        syncWishlistToShopify(mergedItems);
      }
    } else {
      console.warn("[Wishlist] Hydrate response did not include a wishlist array.", {
        endpoint: hydrateUrl,
        customerId,
        responseBody: remoteData || responseText || null
      });
    }
  } catch (err) {
    console.warn("[Wishlist] Hydration request error:", {
      endpoint: WISHLIST_SYNC_ENDPOINT,
      customerId,
      error: err
    });
  }
}

// --- API Helpers ---
async function shopifyFetch(url, body) {
  const res = await fetch(url, {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
  const contentType = (res.headers.get("content-type") || "").toLowerCase();

  // Some apps/proxies return valid JSON with a non-JSON content-type.
  if (contentType.includes("application/json") || contentType.includes("+json")) {
    return res.json();
  }

  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(`${url} expected JSON but received non-JSON response: ${text.slice(0, 120)}`);
  }
}

const cartAdd = (id, quantity = 1) => shopifyFetch("/cart/add.js", { items: [{ id: Number(id), quantity }] });
const removeCartItemByKey = async (key) => {
  const cart = await shopifyFetch("/cart.js");
  const idx = (cart.items || []).findIndex((it) => String(it.key) === String(key));
  if (idx === -1) return; 
  return shopifyFetch("/cart/change.js", { line: idx + 1, quantity: 0 });
};

// --- Caching ---
const ProductCache = new Map();
const ProductInflight = new Map();

async function fetchProduct(handle) {
  const key = String(handle || "");
  if (!key) throw new Error("Missing handle");
  if (ProductCache.has(key)) return ProductCache.get(key);
  if (ProductInflight.has(key)) return ProductInflight.get(key);

  const p = shopifyFetch(`/products/${encodeURIComponent(key)}.js`)
    .then((data) => {
      ProductCache.set(key, data);
      ProductInflight.delete(key);
      return data;
    })
    .catch((err) => {
      ProductInflight.delete(key);
      throw err;
    });

  ProductInflight.set(key, p);
  return p;
}

// --- DOM Utilities ---
const q = (el, sel) => el?.querySelector(sel);
const setText = (el, v) => el && (el.textContent = v == null ? "" : String(v));
const cloneTpl = (id) => document.getElementById(id)?.content.firstElementChild.cloneNode(true);

function getItemFromButton(btn) {
  const getAttr = (name) => btn.getAttribute(name) || btn.closest(`[${name}]`)?.getAttribute(name);
  const url = getAttr("data-url") || getAttr("data-product-url") || btn.closest("[data-product-handle], [data-product-id], .grid-product, .product, article")?.querySelector('a[href*="/products/"]')?.getAttribute("href");
  const handleFromUrl = (u) => {
    try { return new URL(u, window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || null; } 
    catch { return null; }
  };

  const primaryId = getAttr("data-variant-id") || getAttr("data-variant") || getAttr("data-product-id");
  if (!primaryId) return null;

  const handle = getAttr("data-handle") || getAttr("data-product-handle") || handleFromUrl(url);

  return {
    id: String(primaryId),
    variant_id: getAttr("data-variant-id") || getAttr("data-variant"),
    product_id: getAttr("data-product-id"),
    handle: handle ? decodeURIComponent(handle) : null,
    url: url || (handle ? `/products/${handle}` : null),
    title: getAttr("data-title") || getAttr("data-product-title"),
    image: getAttr("data-image") || getAttr("data-product-image"),
    added_at: new Date().toISOString(),
  };
}

function getWishlistToastEl() {
  if (wishlistToastEl && document.body?.contains(wishlistToastEl)) return wishlistToastEl;
  if (!document.body) return null;

  const toast = document.createElement("div");
  toast.className = "WishlistCartToast";
  toast.setAttribute("aria-hidden", "true");
  toast.innerHTML = `
    <div class="WishlistCartToast__panel" role="dialog" aria-modal="true" aria-label="Saved for later">
      <p class="WishlistCartToast__text">Saved for later! Find your saved items in the cart.</p>
      <button type="button" class="WishlistCartToast__link" data-wishlist-toast-cart>View cart</button>
      <button type="button" class="WishlistCartToast__dismiss" data-wishlist-toast-dismiss>Continue shopping</button>
    </div>
  `;

  const cartLink = toast.querySelector("[data-wishlist-toast-cart]");
  cartLink?.addEventListener("click", (e) => {
    e.preventDefault();
    hideWishlistToast();
    openCartDrawerFromPopup();
  });

  toast.addEventListener("click", (e) => {
    if (e.target === toast) hideWishlistToast();
  });

  toast.querySelector("[data-wishlist-toast-dismiss]")?.addEventListener("click", (e) => {
    e.preventDefault();
    hideWishlistToast();
  });

  document.body.appendChild(toast);
  wishlistToastEl = toast;
  return toast;
}

function hideWishlistToast() {
  if (!wishlistToastEl) return;
  wishlistToastEl.classList.remove("is-visible");
  wishlistToastEl.setAttribute("aria-hidden", "true");
}

function showWishlistToast() {
  if (hasSeenWishlistToast()) return;

  const toast = getWishlistToastEl();
  if (!toast) return;

  setSeenWishlistToast();
  toast.setAttribute("aria-hidden", "false");
  toast.classList.add("is-visible");
  if (wishlistToastTimer) window.clearTimeout(wishlistToastTimer);
  wishlistToastTimer = null;
}

function openCartDrawerFromPopup() {
  const isVisible = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== "hidden" && style.display !== "none" && el.getClientRects().length > 0;
  };

  const triggers = [...document.querySelectorAll(".js-drawer-open-cart")];
  const headerCartIcon =
    triggers.find((el) => el.closest("#SiteHeader") && isVisible(el)) ||
    triggers.find((el) => el.closest("#HeaderWrapper") && isVisible(el)) ||
    triggers.find((el) => isVisible(el)) ||
    triggers[0];

  if (!headerCartIcon) return;

  // Mirror user behavior: trigger the same header icon click.
  window.setTimeout(() => {
    headerCartIcon.click();
  }, 0);
}

// --- Global Actions ---
async function refreshCartDrawerProducts() {
  try {
    const cartData = await shopifyFetch("/cart.js");
    document.dispatchEvent(new CustomEvent("cart:build", { bubbles: true, detail: cartData }));
    window.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: cartData }));

    setTimeout(() => {
      const target = document.getElementById("CartDrawer")?.querySelector("[data-products]");
      if (target) window.Wishlist?.bind?.(target);
      delegateSaveForLaterClicks();
    }, 500);
  } catch (err) {
    console.warn("[CartRefresh] error", err);
  }
}

function delegateSaveForLaterClicks() {
  if (document.__cwDelegated) return;
  document.__cwDelegated = true;

  document.addEventListener("click", async (e) => {
    const btn = e.target?.closest?.("[data-save-for-later]");
    if (!btn || btn.disabled) return;
    
    e.preventDefault();
    e.stopImmediatePropagation();

    const cartKey = btn.getAttribute("data-cart-key");
    const variantId = btn.getAttribute("data-variant-id");
    const cartItemRow = btn.closest('.cart__item');

    btn.disabled = true;

    try {
      if (!hasItem(variantId)) {
        const item = getItemFromButton(btn) || { id: variantId, variant_id: variantId };
        addItem(item);
        dispatchWlEvent("wishlist:added", { item });
      }

      if (cartItemRow) cartItemRow.classList.add('is-leaving');

      await new Promise(resolve => setTimeout(resolve, 500));

      await removeCartItemByKey(cartKey);
      await refreshCartDrawerProducts(); 
      renderSavedGrid();

    } catch (err) {
      console.error("[SaveForLater] Error:", err);
      if (cartItemRow) cartItemRow.classList.remove('is-leaving');
      btn.disabled = false;
    }
  }, true);
}

// --- Rendering Logic ---
async function renderSavedGrid() {
  const drawer = document.getElementById("CartDrawer");
  if (!drawer) return;

  const containers = [
    { grid: drawer.querySelector("[data-cw-inline] [data-cw-saved-grid]"), sub: drawer.querySelector("[data-cw-inline] [data-cw-sub]") },
    { grid: drawer.querySelector("[data-cw-inline-empty] [data-cw-saved-grid]"), sub: drawer.querySelector("[data-cw-inline-empty] [data-cw-sub]") }
  ];

  const { items } = getWl();

  containers.forEach(({ grid, sub }) => {
    if (!grid) return;
    if (sub) sub.textContent = items.length ? `${items.length} saved` : "No saved items yet";
    
    const fragment = document.createDocumentFragment();

    if (!items.length) {
      const empty = document.createElement("div");
      empty.className = "CartWishlistEmptyState";
      empty.textContent = "Your room is empty. Tap the heart on products you love.";
      grid.replaceChildren(empty);
      return;
    }

    items.forEach((it) => {
      const card = cloneTpl("CartWishlistTplSavedItem");
      if (!card) return;

      card.dataset.id = String(it.id);
      card.classList.add('is-entering');

      const url = it.url || (it.handle ? `/products/${it.handle}` : "#");
      const titleLink = q(card, "[data-cw-title-link]");
      const img = q(card, "[data-cw-img]");

      if (titleLink) { titleLink.href = url; titleLink.textContent = it.title || "Saved item"; }
      
      if (img) {
        const incomingSrc = it.image || "";
        if (img.src !== incomingSrc) {
          img.style.opacity = '0';
          img.src = incomingSrc;
          img.onload = () => img.style.opacity = '1';
        } else {
          img.style.opacity = '1';
        }
        img.alt = it.title || "Saved item";
      }

      q(card, "[data-cw-remove]")?.addEventListener("click", (e) => {
        e.preventDefault();
        card.classList.add('is-leaving');
        setTimeout(() => {
          removeItem(it.id);
          dispatchWlEvent("wishlist:removed", { id: it.id });
        }, 400);
      });

      hydrateWishlistCard(card, it).catch(console.warn);
      fragment.appendChild(card);
    });

    grid.replaceChildren(fragment);
  });
}

async function hydrateWishlistCard(card, it) {
  const sizeSelect = q(card, "[data-cw-size-select]");
  const priceEl = q(card, "[data-cw-price]");
  const addBtn = q(card, "[data-cw-add]");
  const statusEl = q(card, "[data-cw-status]");

  if (addBtn) addBtn.disabled = true;
  
  const handle = it.handle || (it.url ? new URL(it.url, window.location.origin).pathname.split("/").pop() : null);
  if (!handle) return setText(statusEl, "Out of stock");

  try {
    const productJs = await fetchProduct(handle);
    const variants = productJs.variants || [];
    const options = productJs.options || [];
    
    const sizeIdx = options.findIndex(n => {
      const label = (typeof n === 'string' ? n : n.name).toLowerCase();
      return label.includes("size") || label.includes("sizing");
    });

    const getOpt = (v, idx) => v?.[`option${idx + 1}`] || v?.options?.[idx];
    const availableVariants = variants.filter(v => v.available);

    if (availableVariants.length === 0) {
      if (sizeSelect) sizeSelect.style.display = "none";
      if (addBtn) addBtn.style.display = "none";
      return setText(statusEl, "Sold out");
    }

    if (sizeSelect) {
      sizeSelect.replaceChildren();
      sizeSelect.appendChild(new Option("Select size", "", true, true));

      if (sizeIdx === -1) {
        availableVariants.forEach(v => sizeSelect.appendChild(new Option(v.title, v.id)));
      } else {
        const availableSizes = [...new Set(availableVariants.map(v => getOpt(v, sizeIdx)))];
        availableSizes.forEach(sizeVal => {
          const v = availableVariants.find(variant => getOpt(variant, sizeIdx) === sizeVal);
          sizeSelect.appendChild(new Option(sizeVal, v.id));
        });
      }
      sizeSelect.value = "";
      setText(statusEl, "Select a size");
    }

    const updatePrice = (vId) => {
      if (!vId) {
        if (priceEl) priceEl.textContent = "";
        if (addBtn) addBtn.disabled = true;
        setText(statusEl, "Select a size");
        return;
      }

      const v = variants.find(x => String(x.id) === String(vId));
      if (priceEl && v) priceEl.textContent = `$${(v.price / 100).toFixed(2)}`;
      if (addBtn) addBtn.disabled = !v?.available;
      if (!v?.available) setText(statusEl, "Unavailable");
      else setText(statusEl, "");
    };

    updatePrice(sizeSelect ? "" : availableVariants[0].id);
    if (sizeSelect) sizeSelect.addEventListener("change", () => updatePrice(sizeSelect.value));

    if (addBtn) {
      addBtn.addEventListener("click", async (e) => {
        e.preventDefault();
        const vId = sizeSelect ? sizeSelect.value : availableVariants[0].id;
        if (!vId) {
          setText(statusEl, "Select a size");
          return;
        }

        addBtn.disabled = true;
        setText(statusEl, "Adding...");

        try {
          await cartAdd(vId, 1);
          card.classList.add('is-leaving');
          setTimeout(() => {
            removeItem(it.id);
            dispatchWlEvent("wishlist:removed", { id: it.id });
            refreshCartDrawerProducts();
          }, 450);
        } catch (err) {
          addBtn.disabled = sizeSelect ? !sizeSelect.value : false;
          setText(statusEl, "Try again");
        }
      });
    }
  } catch (err) {
    setText(statusEl, "Error");
  }
}

// --- Initialization ---
(() => {
  const getWishlistButtonLabel = (item, active) => {
    const itemTitle = item?.title ? ` ${item.title}` : "";
    return active ? `Remove${itemTitle} from wishlist` : `Add${itemTitle} to wishlist`;
  };

  function bindWishlistButtons(root = document) {
    root.querySelectorAll("[data-wishlist]:not(.__bound)").forEach((btn) => {
      btn.classList.add("__bound");
      const item = getItemFromButton(btn);
      if (!item) return;

      const updateState = () => {
        const active = hasItem(item.id);
        btn.setAttribute("aria-pressed", active);
        btn.setAttribute("aria-label", getWishlistButtonLabel(item, active));
        btn.toggleAttribute("data-active", active);
        const defaultText = btn.dataset.wishlistDefaultText;
        const savedText = btn.dataset.wishlistSavedText;
        if (defaultText || savedText) {
          btn.textContent = active ? (savedText || "Saved") : (defaultText || "Add to Wishlist");
        }
      };
      updateState();

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const { exists } = toggleItem(item);
        updateState();
        dispatchWlEvent(exists ? "wishlist:removed" : "wishlist:added", { item });
      });
    });
  }

  function init() {
    bindWishlistButtons();
    renderSavedGrid();
    delegateSaveForLaterClicks();
    syncSavedCartBadge();
    
    // Hydrate cloud data into local storage on startup
    hydrateWishlist();

    document.addEventListener("shopify:section:load", (e) => bindWishlistButtons(e.target));
    window.addEventListener("wishlist:change", renderSavedGrid);
    window.addEventListener("wishlist:added", showWishlistToast);
    window.addEventListener("storage", (e) => {
      if (e.key === WISHLIST_KEY || e.key === null) syncSavedCartBadge();
    });

    window.Wishlist = {
      bind: bindWishlistButtons,
      clear() {
        setWl({ items: [] });
        dispatchWlEvent("wishlist:cleared", { items: [] });
      },
    };
  }

  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", init) : init();

})();

// --- Scroll Logic ---
document.addEventListener('click', function(e) {
  const scrollBtn = e.target.closest('.save-for-later-scroll');
  if (!scrollBtn) return;
  e.preventDefault();

  const drawer = document.querySelector('#CartDrawer');
  const scrollContainer = drawer?.querySelector('.drawer__scrollable');
  const targetSection = drawer?.querySelector('.CartWishlistInline');

  if (scrollContainer && targetSection) {
    scrollContainer.scrollTo({
      top: targetSection.offsetTop - 150,
      behavior: 'smooth'
    });
  }
});
